import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, IconDefinition, faBellSlash } from '@fortawesome/free-solid-svg-icons';
import { SavedSearch } from '@enview/interface/types/BillSearch';
import { TeamAPI } from '../../api';
import { useDispatch } from 'react-redux';
import { openSavedSearchAlertModal } from '../../dux';
import { OrganizationUser } from '@enview/interface/types/OrganizationUser';
import CloseIcon from '../svg/CloseIcon';
import { SAVED_SEARCH_RESULTS_LIMIT } from '../../utils/constants';

type SavedSearchAlertBellProps = {
  savedSearch: SavedSearch;
  teamId: number;
  orgUser: OrganizationUser;
  className?: string;
  showModalOnAdd?: boolean;
  resultCount?: number;
};

export const SavedSearchAlertBell = (props: SavedSearchAlertBellProps) => {
  const [updateSavedSearch] = TeamAPI.endpoints.updateSavedSearchAlerts.useMutation();
  const [runSavedSearch] = TeamAPI.endpoints.runSavedSearch.useMutation();
  const dispatch = useDispatch();
  const classes = `btn-text ${props.className}`;
  const { id, subscribedToByUserIds: usersSubscriptions } = props.savedSearch;
  const subscribedUserIds = usersSubscriptions
    ?.filter(
      (userSearch: { isSubscribed: boolean | undefined }) => userSearch.isSubscribed
    )
    ?.map((user: { userId: number }) => user.userId);
  const [isSubscribed, setSubscribed] = useState(subscribedUserIds?.includes(props.orgUser?.id) ?? false);
  const [showLargeSearchError, setShowLargeSearchError] = useState(false);
  const bellIcon = isSubscribed ? faBell : faBellSlash;

  async function updateSearch() {
    const subscribing = !isSubscribed;
    if (subscribing && props.resultCount !== undefined && props.resultCount > SAVED_SEARCH_RESULTS_LIMIT) {
      setShowLargeSearchError(true);
      setTimeout(() => setShowLargeSearchError(false), 7000);
      return;
    }
    const update = {
      isSubscribed: subscribing
    }
    if (props.teamId && props.orgUser?.id) {
      const result = await updateSavedSearch({
        teamId: props.teamId,
        savedSearchId: id,
        orgUser: props.orgUser,
        savedSearchUserSubscriptionUpdate: update
      });
      if ('error' in result) {
        if (subscribing) {
          setShowLargeSearchError(true);
          setTimeout(() => setShowLargeSearchError(false), 7000);
        }
        return;
      }
      setSubscribed(update.isSubscribed);
      dispatch(openSavedSearchAlertModal(update.isSubscribed, false));
      // If user has subscribed, run saved search now so results can be saved and compared against in the future
      if (update.isSubscribed) {
        await runSavedSearch({
          teamId: props.teamId,
          savedSearchId: id,
          userId: props.orgUser.id
        });
      }
    }
  }

  return (
    <div>
      <button
        className={classes}
        onClick={updateSearch}
        type="button">
        <FontAwesomeIcon color="#27A4CC" icon={bellIcon as IconDefinition} />
      </button>
      {showLargeSearchError && (
        <div className="confirmation-popup">
          <div>
            <CloseIcon className="float-right" onClick={() => setShowLargeSearchError(false)} />
          </div>
          <h3 className="title">Error</h3>
          <p>
            This search has more than {SAVED_SEARCH_RESULTS_LIMIT.toLocaleString()} results. Please refine this search further
            before subscribing to new bill updates.
          </p>
        </div>
      )}
    </div>
  );
};
